import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getRightInset } from '@/lib/viewportHelpers';
import { usePartnrPanelStore } from '@/stores/partnrPanelStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { Plus, Minus, Scan } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CanvasControl, CanvasControlGroup, CONTROL_ICON } from './CanvasControl';
import {
  FIT_PADDING_TOP,
  FIT_PADDING_LEFT,
  FIT_PADDING_RIGHT,
  FIT_PADDING_BOTTOM,
  MIN_ITEM_Y,
} from '@/lib/canvasConstants';

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 4;
const ZOOM_INCREMENT = 0.1; // 10% increments
const AUTO_HIDE_DELAY = 1500; // ms before notification hides
// The zoom pill shows when the pointer is this close to the bottom-right corner
const REVEAL_X = 160;
const REVEAL_Y = 280;
const REVEAL_HIDE_DELAY = 500;
// Touch has no hover: a tap near the corner (or a pinch zoom) shows it this long
const TOUCH_HOLD = 3000;

// The corner moves left of the docked Partnr panel while it is open (inset 0 otherwise)
const nearCorner = (x: number, y: number) => {
  const fromEdge = window.innerWidth - getRightInset() - x;
  return fromEdge >= 0 && fromEdge < REVEAL_X && window.innerHeight - y < REVEAL_Y;
};

/**
 * Whether the zoom pill is out. Mouse: shown while the pointer is near the bottom-right corner, hidden half
 * a second after it leaves. Touch and pen: a tap near the corner, or a pinch
 * zoom, shows it for a few seconds; taps on the pill itself land in the
 * corner too, so using it keeps it up. Tracks pointer position instead of
 * covering the corner with a hover zone, which would eat clicks and pans on
 * the canvas underneath. Goes by the input actually used, so an iPad with a
 * trackpad gets the mouse behaviour.
 */
function useCornerReveal(zoom: number): boolean {
  const [shown, setShown] = useState(false);
  const shownRef = useRef(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastInput = useRef<'mouse' | 'touch'>('mouse');

  const set = useCallback((next: boolean) => {
    if (shownRef.current !== next) setShown((shownRef.current = next));
  }, []);
  const hideIn = useCallback(
    (ms: number) => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => {
        hideTimer.current = null;
        set(false);
      }, ms);
    },
    [set],
  );

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      lastInput.current = 'mouse';
      if (nearCorner(e.clientX, e.clientY)) {
        if (hideTimer.current) clearTimeout(hideTimer.current);
        hideTimer.current = null;
        set(true);
      } else if (shownRef.current && !hideTimer.current) {
        hideIn(REVEAL_HIDE_DELAY);
      }
    };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      lastInput.current = 'touch';
      if (nearCorner(e.clientX, e.clientY)) {
        set(true);
        hideIn(TOUCH_HOLD);
      }
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true, capture: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown, { capture: true });
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [set, hideIn]);

  // A pinch zoom on touch brings the controls up briefly (not on first render)
  const lastZoom = useRef(zoom);
  useEffect(() => {
    if (zoom === lastZoom.current) return;
    lastZoom.current = zoom;
    if (lastInput.current === 'touch') {
      set(true);
      hideIn(TOUCH_HOLD);
    }
  }, [zoom, set, hideIn]);

  return shown;
}

// Zoom notification pill (centered, auto-hides)
export function ZoomNotification() {
  const { zoom, viewMode } = useCanvasStore();
  const [isVisible, setIsVisible] = useState(false);
  const [displayZoom, setDisplayZoom] = useState(zoom);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastZoomRef = useRef(zoom);
  
  const percentage = Math.round(displayZoom * 100);
  
  useEffect(() => {
    if (zoom === lastZoomRef.current) return;
    lastZoomRef.current = zoom;
    
    setIsVisible(true);
    setDisplayZoom(zoom);
    
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, AUTO_HIDE_DELAY);
    
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [zoom]);
  
  if (viewMode !== 'focus') return null;

  return (
    <div 
      className={cn(
        'fixed bottom-24 left-1/2 -translate-x-1/2 z-40',
        'transition-all duration-300',
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-2 pointer-events-none'
      )}
    >
      <div className="px-4 py-2 rounded-full bg-background/95 backdrop-blur-sm border border-border shadow-lg">
        <span className="text-sm font-medium text-foreground">{percentage}%</span>
      </div>
    </div>
  );
}

// Zoom controls (+/- buttons with fit-to-view) - positioned in bottom-right, aligned with toolbar
export function ZoomControls() {
  const { zoom, setZoom, setPan, viewMode, currentBoardId, items } = useCanvasStore();
  const revealed = useCornerReveal(zoom);
  const rightInset = usePartnrPanelStore((s) => s.rightInset);
  
  // Zoom centered on the content centroid — keeps items visually stable
  // when using the +/- buttons.  Falls back to the usable-area center
  // (accounting for toolbar/sidebar padding) when there are no items so the
  // view doesn't drift toward the raw viewport center, which is offset from
  // where items actually appear.
  const zoomCentered = useCallback((newZoom: number) => {
    const state = useCanvasStore.getState();
    const { panX, panY, getBoundingBox } = state;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Try to use the content centroid as the focal point
    const bbox = getBoundingBox();
    let focalCanvasX: number;
    let focalCanvasY: number;

    if (bbox) {
      focalCanvasX = bbox.centerX;
      focalCanvasY = bbox.centerY;
    } else {
      // No items — use the usable-area center (asymmetric padding)
      const usableCenterX = FIT_PADDING_LEFT + (viewportWidth - FIT_PADDING_LEFT - FIT_PADDING_RIGHT) / 2;
      const usableCenterY = FIT_PADDING_TOP + (viewportHeight - FIT_PADDING_TOP - FIT_PADDING_BOTTOM) / 2;
      focalCanvasX = usableCenterX / zoom - panX;
      focalCanvasY = usableCenterY / zoom - panY;
    }

    // Where on screen is this focal canvas point currently?
    const focalScreenX = (focalCanvasX + panX) * zoom;
    const focalScreenY = (focalCanvasY + panY) * zoom;

    // Recalculate pan so the same canvas point stays at the same screen position
    const newPanX = focalScreenX / newZoom - focalCanvasX;
    const newPanY = focalScreenY / newZoom - focalCanvasY;

    setZoom(newZoom);
    setPan(newPanX, newPanY);
  }, [zoom, setZoom, setPan]);

  const zoomIn = () => {
    const currentPercent = Math.round(zoom * 100);
    const newPercent = Math.min(currentPercent + 10, ZOOM_MAX * 100);
    zoomCentered(newPercent / 100);
  };

  const zoomOut = () => {
    const currentPercent = Math.round(zoom * 100);
    const newPercent = Math.max(currentPercent - 10, ZOOM_MIN * 100);
    zoomCentered(newPercent / 100);
  };

  const fitToView = useCallback(() => {
    const { getBoundingBox } = useCanvasStore.getState();
    const bounds = getBoundingBox();

    if (!bounds) {
      setPan(0, 0);
      setZoom(1);
      return;
    }

    // Match InfiniteCanvas.fitAllItemsInView: account for UI overlays
    const safeBounds = {
      ...bounds,
      minY: Math.max(bounds.minY, MIN_ITEM_Y),
      centerY: bounds.minY < MIN_ITEM_Y
        ? (MIN_ITEM_Y + bounds.maxY) / 2
        : bounds.centerY,
    };

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const usableWidth = viewportWidth - FIT_PADDING_LEFT - FIT_PADDING_RIGHT;
    const usableHeight = viewportHeight - FIT_PADDING_TOP - FIT_PADDING_BOTTOM;

    const scaleX = usableWidth / safeBounds.width;
    const scaleY = usableHeight / (safeBounds.maxY - safeBounds.minY);
    const newZoom = Math.min(Math.max(Math.min(scaleX, scaleY), ZOOM_MIN), 2);

    const usableCenterX = FIT_PADDING_LEFT + usableWidth / 2;
    const usableCenterY = FIT_PADDING_TOP + usableHeight / 2;

    setZoom(newZoom);
    setPan(usableCenterX / newZoom - safeBounds.centerX, usableCenterY / newZoom - safeBounds.centerY);
  }, [setZoom, setPan]);
  
  const isAtMin = Math.round(zoom * 100) <= ZOOM_MIN * 100;
  const isAtMax = Math.round(zoom * 100) >= ZOOM_MAX * 100;
  
  if (viewMode !== 'focus' || !currentBoardId) return null;

  return (
    <CanvasControlGroup
      style={rightInset ? { right: rightInset + 16 } : undefined}
      className={cn(
        'fixed bottom-6 right-4 z-40 transition-[opacity,transform] duration-200 ease-out',
        // Out of the way until wanted (see useCornerReveal); the keyboard can
        // always reach it (focus-visible, so a mouse click doesn't pin it open)
        revealed
          ? 'opacity-100 translate-x-0'
          : 'pointer-events-none opacity-0 translate-x-2 has-[:focus-visible]:pointer-events-auto has-[:focus-visible]:opacity-100 has-[:focus-visible]:translate-x-0',
      )}
    >
      <CanvasControl label="Zoom in" shortcut="+" onClick={zoomIn} disabled={isAtMax}>
        <Plus className={CONTROL_ICON} />
      </CanvasControl>
      <CanvasControl label="Zoom out" shortcut="−" onClick={zoomOut} disabled={isAtMin}>
        <Minus className={CONTROL_ICON} />
      </CanvasControl>
      <div className="mx-auto my-0.5 h-px w-5 bg-border" />
      <CanvasControl label="Fit to view" shortcut="1" onClick={fitToView}>
        <Scan className={CONTROL_ICON} />
      </CanvasControl>
    </CanvasControlGroup>
  );
}
